import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, SlidersHorizontal, X } from "@phosphor-icons/react";
import PackageCard from "@/components/PackageCard";
import Newsletter from "@/components/Newsletter";
import { PACKAGES, waLink } from "@/lib/site";

const BUDGET_RANGES = [
    { label: "All budgets", min: 0, max: Infinity },
    { label: "Under ₹1.5L", min: 0, max: 150000 },
    { label: "₹1.5L – ₹2L", min: 150000, max: 200000 },
    { label: "₹2L – ₹2.5L", min: 200000, max: 250000 },
    { label: "Above ₹2.5L", min: 250000, max: Infinity },
];

function parsePriceNum(priceStr) {
    // "₹ 2,40,000" → 240000
    return parseInt(priceStr.replace(/[^\d]/g, ""), 10);
}

export default function Packages() {
    const [budgetIdx, setBudgetIdx] = useState(0);
    const [showFilters, setShowFilters] = useState(false);

    const range = BUDGET_RANGES[budgetIdx];
    const filtered = PACKAGES.filter((p) => {
        const num = parsePriceNum(p.price);
        return num >= range.min && num <= range.max;
    });

    return (
        <div data-testid="page-packages" className="pt-28">
            {/* Hero */}
            <section className="py-20 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <p className="overline">Signature Packages</p>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6 tracking-tight max-w-4xl">
                        Six escapes, <em className="italic text-gold">each one considered.</em>
                    </h1>
                    <p className="font-sans text-base text-white/65 mt-6 max-w-2xl leading-relaxed">
                        Flights, hotels and meals — bundled, but never packaged. Every trip below can be tailored
                        around your dates, pace and interests.
                    </p>

                    {/* Filter bar */}
                    <div className="mt-10 flex flex-wrap items-center gap-4 border-b border-white/10 pb-6">
                        <button
                            onClick={() => setShowFilters((v) => !v)}
                            className="inline-flex items-center gap-2 border border-white/20 hover:border-gold hover:text-gold px-4 py-2 text-xs uppercase tracking-widest transition-colors"
                        >
                            <SlidersHorizontal size={14} weight="light" />
                            Filter by budget
                        </button>
                        {budgetIdx !== 0 && (
                            <button
                                onClick={() => setBudgetIdx(0)}
                                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-gold transition-colors uppercase tracking-widest"
                            >
                                <X size={12} /> Clear filter
                            </button>
                        )}
                        <p className="ml-auto font-sans text-sm text-white/50">
                            <span className="text-white">{filtered.length}</span> of {PACKAGES.length} packages
                        </p>
                    </div>

                    {showFilters && (
                        <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-4 flex flex-wrap gap-2"
                        >
                            {BUDGET_RANGES.map((b, i) => (
                                <button
                                    key={b.label}
                                    onClick={() => { setBudgetIdx(i); setShowFilters(false); }}
                                    className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                                        budgetIdx === i
                                            ? "bg-gold text-navy border-gold"
                                            : "border-white/20 text-white/70 hover:border-gold hover:text-gold"
                                    }`}
                                >
                                    {b.label}
                                </button>
                            ))}
                        </motion.div>
                    )}
                </div>
            </section>

            <section className="pb-24 lg:pb-32 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    {filtered.length > 0 ? (
                        filtered.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)
                    ) : (
                        <div className="border border-white/10 p-16 text-center mt-8">
                            <p className="font-serif text-2xl">No packages in that range.</p>
                            <p className="font-sans text-sm text-white/55 mt-2">Try a different budget filter.</p>
                            <button
                                onClick={() => setBudgetIdx(0)}
                                className="mt-6 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158]"
                            >
                                Show all packages <ArrowRight size={12} weight="bold" />
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Bespoke CTA */}
            <section className="py-20 bg-[#080E20] border-t border-white/5" data-testid="packages-bespoke">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid lg:grid-cols-12 gap-10 items-center border border-white/10 p-10 lg:p-14">
                        <div className="lg:col-span-8">
                            <p className="overline">Something more personal?</p>
                            <h2 className="font-serif text-3xl sm:text-4xl font-medium mt-4 leading-tight">
                                We build <em className="italic text-gold">bespoke itineraries</em> too.
                            </h2>
                            <p className="font-sans text-sm text-white/60 mt-4 leading-relaxed max-w-xl">
                                None of our packages quite fit? Tell us your dream trip — destination, duration, budget, vibe — and we'll craft something entirely yours within 48 hours.
                            </p>
                            <ul className="mt-6 grid sm:grid-cols-3 gap-4">
                                {["Honeymoons", "Family holidays", "Solo adventures", "Group travel", "Anniversary trips", "Bucket-list journeys"].map((item) => (
                                    <li key={item} className="font-sans text-sm text-white/60">— {item}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-4 flex flex-col gap-3">
                            <a
                                href={waLink("Hi! I'd like a custom itinerary built for me.")}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-7 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                Request bespoke trip <ArrowRight size={14} weight="bold" />
                            </a>
                            <a
                                href="/contact"
                                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-gold hover:text-gold px-7 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                Send a brief
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <Newsletter />
        </div>
    );
}
